/**
 * @fileoverview Utilitaire de gestion du stockage local pour la company sélectionnée
 *
 * Gère la persistance de la company active par device/navigateur.
 * Permet à chaque session de maintenir sa propre company sélectionnée,
 * indépendamment des switches effectués sur d'autres devices.
 *
 * Architecture :
 * - localStorage stocke la company sélectionnée pour CE device
 * - La BDD stocke la dernière company utilisée globalement (pour le login)
 * - Ce module fait le pont entre les deux
 *
 * @module companyStorage
 * @author Équipe Brydge
 * @since 2.4.0
 */

import {localStorageAvailable} from './storageAvailable';

/**
 * Clé utilisée dans le localStorage pour stocker l'ID de la company sélectionnée
 * @constant {string}
 */
const SELECTED_COMPANY_KEY = 'brydge_selected_company_id';

/**
 * Clé utilisée pour stocker les métadonnées de la company (nom, slug)
 * Permet d'afficher des informations même avant le chargement API
 * @constant {string}
 */
const SELECTED_COMPANY_META_KEY = 'brydge_selected_company_meta';

/**
 * Récupère l'ID de la company sélectionnée dans le localStorage
 *
 * Cette valeur représente la company sur laquelle l'utilisateur travaille
 * actuellement sur CE device, indépendamment de ce qui est stocké en BDD.
 *
 * @return {string|null} ID de la company sélectionnée ou null si non défini
 *
 * @example
 * const companyId = getSelectedCompanyId();
 * if (companyId) {
 *   // Utiliser cette company pour les requêtes API
 * }
 */
export const getSelectedCompanyId = () => {
  if (!localStorageAvailable()) {
    return null;
  }

  try {
    const value = localStorage.getItem(SELECTED_COMPANY_KEY);
    return value || null;
  } catch (error) {
    console.error('[companyStorage] Erreur lecture localStorage:', error);
    return null;
  }
};

/**
 * Enregistre la company sélectionnée dans le localStorage
 *
 * Appelée lors d'un switch de company ou à l'initialisation après login.
 * Les métadonnées permettent d'afficher le nom de la company immédiatement
 * sans attendre le chargement API.
 *
 * @param {string} companyId - ID de la company à sélectionner
 * @param {Object} [meta] - Métadonnées optionnelles de la company
 * @param {string} [meta.name] - Nom de la company
 * @param {string} [meta.slug] - Slug de la company
 * @return {boolean} True si l'enregistrement a réussi
 *
 * @example
 * // Lors d'un switch de company
 * setSelectedCompany(newCompanyId, { name: 'Hotel Paris', slug: 'hotel-paris' });
 */
export const setSelectedCompany = (companyId, meta = null) => {
  if (!localStorageAvailable()) {
    return false;
  }

  if (!companyId) {
    console.warn('[companyStorage] Tentative de set avec companyId null/undefined');
    return false;
  }

  try {
    localStorage.setItem(SELECTED_COMPANY_KEY, companyId);

    if (meta) {
      localStorage.setItem(SELECTED_COMPANY_META_KEY, JSON.stringify({
        id: companyId,
        name: meta.name || null,
        slug: meta.slug || null,
        updatedAt: new Date().toISOString(),
      }));
    }

    return true;
  } catch (error) {
    console.error('[companyStorage] Erreur écriture localStorage:', error);
    return false;
  }
};

/**
 * Supprime la company sélectionnée du localStorage
 *
 * Appelée lors de la déconnexion pour nettoyer l'état local.
 * Au prochain login, la company sera déterminée par activeCompanyId en BDD.
 *
 * @return {boolean} True si la suppression a réussi
 *
 * @example
 * // Lors du logout
 * clearSelectedCompany();
 */
export const clearSelectedCompany = () => {
  if (!localStorageAvailable()) {
    return false;
  }

  try {
    localStorage.removeItem(SELECTED_COMPANY_KEY);
    localStorage.removeItem(SELECTED_COMPANY_META_KEY);
    return true;
  } catch (error) {
    console.error('[companyStorage] Erreur suppression localStorage:', error);
    return false;
  }
};

export default {
  getSelectedCompanyId,
  setSelectedCompany,
  clearSelectedCompany,
};
